// Talk-track editor (/talk-tracks/:topicId): one topic's versions. Content
// tabs edit the selected version when it's a draft (read-only otherwise);
// Performance / Test / History measure, try and trace it. The right panel
// drives the lifecycle: checks → publish (→ approve first when review is
// required) → rollout → promote.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  deleteTopic,
  discardDraft,
  ensureDraft,
  errorText,
  getMeta,
  getTopic,
  getTopicStats,
  getVersion,
  saveDraft,
  updateTopic,
} from "../services/talkTracks";
import { listGuides } from "../services/guides";
import { emptyContent } from "../types/talkTrack";
import type {
  ContentCheck,
  StatsFilters,
  TalkTrackComment,
  TalkTrackContent,
  TalkTrackMeta,
  TopicDetail,
  TopicStats,
  UnitStats,
  VersionDetail,
} from "../types/talkTrack";
import type { CharacterProfile } from "../types/guide";
import { RANGE_PRESETS, type RangePreset } from "../components/analytics/format";
import {
  NO_FILTERS,
  allUnitIds,
  defaultVersion,
  filtersActive,
  statusLabel,
  unitDomId,
  unitTab,
} from "../components/talkTracks/format";
import { UnitContext, type UnitContextValue } from "../components/talkTracks/unitContext";
import { TopicBadges } from "../components/talkTracks/TopicBadges";
import { TopicSettings } from "../components/talkTracks/TopicSettings";
import { ExploreEditor } from "../components/talkTracks/ExploreEditor";
import { IntroPolicyEditor } from "../components/talkTracks/IntroPolicyEditor";
import { ExplainersEditor } from "../components/talkTracks/ExplainersEditor";
import { PatternsEditor } from "../components/talkTracks/PatternsEditor";
import { ModulesEditor } from "../components/talkTracks/ModulesEditor";
import { SafetyScreensEditor } from "../components/talkTracks/SafetyScreensEditor";
import { PerformanceTab } from "../components/talkTracks/PerformanceTab";
import { TalkTrackBench } from "../components/talkTracks/TalkTrackBench";
import { HistoryTab } from "../components/talkTracks/HistoryTab";
import { ReviewPanel } from "../components/talkTracks/ReviewPanel";
import { RolloutPanel } from "../components/talkTracks/RolloutPanel";
import { SimulationPanel } from "../components/talkTracks/SimulationPanel";

type Tab =
  | "explore"
  | "policy"
  | "patterns"
  | "explainers"
  | "modules"
  | "screens"
  | "performance"
  | "test"
  | "history";

const TABS: { key: Tab; label: string }[] = [
  { key: "explore", label: "Explore" },
  { key: "patterns", label: "Patterns" },
  { key: "explainers", label: "Explainers" },
  { key: "modules", label: "Modules" },
  { key: "screens", label: "Safety screens" },
  { key: "performance", label: "Performance" },
  { key: "test", label: "Test" },
  { key: "history", label: "History" },
];
// An introduction topic: life areas and the policy, no playbook units.
const INTRO_TABS: { key: Tab; label: string }[] = [
  { key: "explore", label: "Areas" },
  { key: "policy", label: "Policy" },
  { key: "performance", label: "Performance" },
  { key: "test", label: "Test" },
  { key: "history", label: "History" },
];

function groupBy<T>(items: T[], key: (item: T) => string | null): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    if (!k) continue;
    const list = out.get(k);
    if (list) list.push(item);
    else out.set(k, [item]);
  }
  return out;
}

export function TalkTrackEditorPage() {
  const { topicId = "" } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [meta, setMeta] = useState<TalkTrackMeta | null>(null);
  const [metaFailed, setMetaFailed] = useState(false);
  const [guides, setGuides] = useState<CharacterProfile[]>([]);
  const [detail, setDetail] = useState<TopicDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Deep-linkable: ?v=3&tab=performance
  const [selected, setSelected] = useState<number | null>(() => {
    const v = parseInt(searchParams.get("v") || "", 10);
    return Number.isFinite(v) ? v : null;
  });
  const [tab, setTab] = useState<Tab>(() => {
    const wanted = searchParams.get("tab");
    return [...TABS, ...INTRO_TABS].find((t) => t.key === wanted)?.key ?? "explore";
  });
  const [versionDetail, setVersionDetail] = useState<VersionDetail | null>(null);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [content, setContent] = useState<TalkTrackContent>(emptyContent());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [filters, setFilters] = useState<StatsFilters>(NO_FILTERS);
  const [stats, setStats] = useState<TopicStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsKey, setStatsKey] = useState(0);
  const [hasPassingSim, setHasPassingSim] = useState<boolean | null>(null);
  const [focusedUnit, setFocusedUnit] = useState<string | null>(null);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);

  // Latest editor content / dirty flag for async callbacks (a save or a
  // rollout change must not clobber edits made while it was in flight).
  const contentRef = useRef(content);
  const dirtyRef = useRef(dirty);
  useEffect(() => {
    contentRef.current = content;
    dirtyRef.current = dirty;
  }, [content, dirty]);

  const setStatusMsg = useCallback((text: string, isError = false) => {
    setStatus(text);
    setStatusError(isError);
  }, []);

  useEffect(() => {
    getMeta()
      .then(setMeta)
      .catch((e) => {
        setMetaFailed(true);
        setStatusMsg(`Talk-track settings unavailable: ${errorText(e)}`, true);
      });
    listGuides()
      .then(setGuides)
      .catch(() => setGuides([]));
  }, [setStatusMsg]);

  // Topic + version list; lands on the draft (or the most relevant version).
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setLoadError(null);
    getTopic(topicId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setSelected((cur) =>
          cur !== null && d.versions.some((v) => v.version === cur) ? cur : defaultVersion(d),
        );
      })
      .catch((e) => !cancelled && setLoadError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [topicId]);

  const applyVersion = useCallback((d: VersionDetail) => {
    setVersionDetail(d);
    setContent(d.version.content);
    setDirty(false);
  }, []);

  useEffect(() => {
    if (selected === null) return;
    let cancelled = false;
    setVersionDetail(null);
    setVersionError(null);
    setHasPassingSim(null);
    getVersion(topicId, selected)
      .then((d) => !cancelled && applyVersion(d))
      .catch((e) => !cancelled && setVersionError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [topicId, selected, applyVersion]);

  // Keep ?v= / ?tab= in the URL for deep links.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selected !== null) next.set("v", String(selected));
        next.set("tab", tab);
        return next;
      },
      { replace: true },
    );
  }, [selected, tab, setSearchParams]);

  const days = RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 30;
  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);
    getTopicStats(topicId, days, filters)
      .then((s) => !cancelled && setStats(s))
      .catch((e) => !cancelled && setStatsError(`Performance unavailable: ${errorText(e)}`))
      .finally(() => !cancelled && setStatsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [topicId, days, statsKey, filters]);

  // Warn before leaving the page with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Scroll to a unit once its tab has rendered; the highlight fades after.
  useEffect(() => {
    if (!scrollTarget) return;
    document.getElementById(unitDomId(scrollTarget))?.scrollIntoView({ behavior: "smooth", block: "center" });
    setScrollTarget(null);
  }, [scrollTarget, tab]);
  useEffect(() => {
    if (!focusedUnit) return;
    const timer = setTimeout(() => setFocusedUnit(null), 2500);
    return () => clearTimeout(timer);
  }, [focusedUnit]);

  const topic = detail?.topic ?? null;
  const isIntro = topic?.kind === "introduction";
  const version = versionDetail?.version ?? null;
  const editable = version?.status === "draft";
  const checks = useMemo(() => versionDetail?.checks ?? [], [versionDetail]);
  const comments = useMemo(() => versionDetail?.comments ?? [], [versionDetail]);
  const draftVersion = detail?.versions.find((v) => v.status === "draft")?.version ?? null;
  const unitIds = useMemo(() => allUnitIds(content), [content]);

  const changeContent = useCallback((next: TalkTrackContent) => {
    setContent(next);
    setDirty(true);
  }, []);

  function selectVersion(v: number) {
    if (v === selected) return;
    if (dirty && !confirm(`Discard unsaved changes to v${selected}?`)) return;
    setSelected(v);
  }

  const focusUnit = useCallback(
    (unitId: string) => {
      const where = unitTab(content, unitId);
      if (!where) {
        setStatusMsg(`${unitId} isn't part of v${selected}.`, true);
        return;
      }
      setTab(where);
      setFocusedUnit(unitId);
      setScrollTarget(unitId);
    },
    [content, selected, setStatusMsg],
  );

  const reloadTopic = useCallback(async () => {
    const d = await getTopic(topicId);
    setDetail(d);
    return d;
  }, [topicId]);

  // After any lifecycle change: refresh the topic, then either switch to
  // `select` or reload the selected version (unless it has unsaved edits).
  const afterChange = useCallback(
    async (message: string, select?: number) => {
      setStatusMsg(message);
      try {
        await reloadTopic();
        if (select !== undefined && select !== selected) setSelected(select);
        else if (selected !== null && !dirtyRef.current) applyVersion(await getVersion(topicId, selected));
      } catch (e) {
        setStatusMsg(`${message} — but reloading failed: ${errorText(e)}`, true);
      }
      setStatsKey((k) => k + 1);
    },
    [reloadTopic, selected, topicId, applyVersion, setStatusMsg],
  );

  async function save() {
    if (!version || !editable) return;
    const sent = content;
    setSaving(true);
    try {
      const d = await saveDraft(topicId, version.version, sent);
      if (contentRef.current === sent) {
        applyVersion(d);
      } else {
        // Edited while saving: keep the newer edits (still dirty), take the checks.
        setVersionDetail(d);
      }
      const errors = d.checks.filter((c) => c.level === "error").length;
      const warnings = d.checks.length - errors;
      setStatusMsg(
        `Saved v${d.version.version} ✓ — ${errors ? `${errors} blocking error${errors === 1 ? "" : "s"}` : "no blocking errors"}` +
          `, ${warnings} warning${warnings === 1 ? "" : "s"}`,
        false,
      );
    } catch (e) {
      setStatusMsg(`Save failed: ${errorText(e)}`, true);
    } finally {
      setSaving(false);
    }
  }

  async function startDraft() {
    if (draftVersion !== null) return selectVersion(draftVersion);
    if (dirty && !confirm("Discard unsaved changes?")) return;
    try {
      const draft = await ensureDraft(topicId);
      await afterChange(
        `Draft v${draft.version} started${draft.basedOn ? ` from v${draft.basedOn}` : ""}`,
        draft.version,
      );
      setTab((t) => (t === "performance" || t === "history" ? "explore" : t));
    } catch (e) {
      setStatusMsg(`Couldn't start a draft: ${errorText(e)}`, true);
    }
  }

  async function discard() {
    if (!version || !editable || !detail) return;
    if (!confirm(`Discard draft v${version.version}? Its edits and review comments are deleted.`)) return;
    try {
      await discardDraft(topicId, version.version);
      const fallback =
        detail.topic.liveVersion ?? detail.versions.find((v) => v.version !== version.version)?.version;
      await afterChange(`Draft v${version.version} discarded`, fallback);
    } catch (e) {
      setStatusMsg(`Couldn't discard the draft: ${errorText(e)}`, true);
    }
  }

  async function removeTopic() {
    if (!topic) return;
    if (!confirm(`Delete "${topic.name}"? This can't be undone.`)) return;
    try {
      await deleteTopic(topic.id);
      navigate("/talk-tracks");
    } catch (e) {
      setStatusMsg(`Couldn't delete the topic: ${errorText(e)}`, true);
    }
  }

  async function toggleEnabled() {
    if (!topic) return;
    if (topic.enabled && !confirm(`Disable "${topic.name}"? No guide will use this topic until it's re-enabled.`)) return;
    try {
      const updated = await updateTopic(topic.id, { enabled: !topic.enabled });
      setDetail((d) => (d ? { ...d, topic: updated } : d));
      setStatusMsg(updated.enabled ? "Topic enabled ✓" : "Topic disabled — guides stop using it");
    } catch (e) {
      setStatusMsg(`Update failed: ${errorText(e)}`, true);
    }
  }

  function goBack() {
    if (dirty && !confirm("Leave without saving your changes?")) return;
    navigate("/talk-tracks");
  }

  const onComment = useCallback((comment: TalkTrackComment) => {
    setVersionDetail((d) => {
      if (!d) return d;
      const exists = d.comments.some((c) => c.id === comment.id);
      return {
        ...d,
        comments: exists ? d.comments.map((c) => (c.id === comment.id ? comment : c)) : [...d.comments, comment],
      };
    });
  }, []);

  const onChecks = useCallback((next: ContentCheck[]) => {
    setVersionDetail((d) => (d ? { ...d, checks: next } : d));
  }, []);

  const onError = useCallback((m: string) => setStatusMsg(m, true), [setStatusMsg]);

  const unitCtx = useMemo<UnitContextValue | null>(() => {
    if (!version) return null;
    return {
      topicId,
      version: version.version,
      editable,
      statsByUnit: new Map<string, UnitStats>((stats?.units ?? []).map((u) => [u.unitId, u])),
      checksByUnit: groupBy(checks, (c) => c.unitId),
      commentsByUnit: groupBy(comments, (c) => c.unitId),
      focusedUnit,
      onComment,
      onError,
    };
  }, [topicId, version, editable, stats, checks, comments, focusedUnit, onComment, onError]);

  function renderTab() {
    if (!topic || selected === null) return null;
    if (tab === "performance") {
      return (
        // Keyed by range + filters: a drill-down fetched under other filters
        // would no longer match the table.
        <PerformanceTab key={`${preset}-${filters.inputMode}-${filters.excludeTopUser}`}
          topic={topic} stats={stats} loading={statsLoading} error={statsError}
          preset={preset} onPreset={setPreset} filters={filters} onFilters={setFilters}
          unitIds={unitIds} onFocusUnit={focusUnit} />
      );
    }
    if (versionError) return <div className="state-box error">Couldn't load v{selected}: {versionError}</div>;
    if (!version || !unitCtx) return <div className="state-box">Loading v{selected}…</div>;
    if (tab === "test") {
      return <TalkTrackBench key={`${topicId}-${version.version}`} topicId={topicId} version={version.version}
        dirty={dirty} guides={guides} />;
    }
    if (tab === "history") {
      return (
        <HistoryTab topic={topic} versions={detail?.versions ?? []} selectedVersion={version.version}
          content={content} dirty={dirty} onSelect={selectVersion} onFocusUnit={focusUnit} onDone={afterChange} />
      );
    }
    return (
      <UnitContext.Provider value={unitCtx}>
        {!editable && (
          <div className="callout tt-callout">
            v{version.version} is {statusLabel(version.status)} and read-only.{" "}
            {draftVersion !== null ? `Edit draft v${draftVersion} to make changes.` : "Click Edit to start a draft."}
          </div>
        )}
        {tab === "explore" && (
          <ExploreEditor topicId={topicId} content={content} dimensions={meta?.dimensions ?? []} onChange={changeContent}
            intro={
              isIntro
                ? {
                    lifeAreas: meta?.lifeAreas ?? [],
                    sensitivities: meta?.sensitivities ?? [],
                    probeRungs: meta?.probeRungs ?? [],
                  }
                : undefined
            } />
        )}
        {tab === "policy" && <IntroPolicyEditor content={content} onChange={changeContent} />}
        {tab === "patterns" && <PatternsEditor topicId={topicId} content={content} onChange={changeContent} />}
        {tab === "explainers" && <ExplainersEditor topicId={topicId} content={content} onChange={changeContent} />}
        {tab === "modules" && (
          <ModulesEditor topicId={topicId} content={content} moduleTypes={meta?.moduleTypes ?? []}
            protocols={meta?.protocols ?? []} onChange={changeContent} />
        )}
        {tab === "screens" && (
          <SafetyScreensEditor topicId={topicId} content={content}
            requiresSafetyScreen={topic.requiresSafetyScreen} onChange={changeContent} />
        )}
      </UnitContext.Provider>
    );
  }

  const tabCount = (t: Tab): string => {
    if (t === "explore") return ` (${content.tracks.length})`;
    if (t === "patterns") return ` (${(content.patterns ?? []).length})`;
    if (t === "explainers") return ` (${content.explainers.length})`;
    if (t === "modules") return ` (${content.modules.length})`;
    if (t === "screens") return ` (${content.safetyScreens.length})`;
    return "";
  };

  return (
    <div className="editor-page">
      <div className="editor-main">
        <a className="back-link" onClick={goBack} style={{ cursor: "pointer" }}>← All talk tracks</a>

        {loadError ? (
          <div className="state-box error">Couldn't load “{topicId}”: {loadError}</div>
        ) : !detail || !topic ? (
          <div className="state-box">Loading talk track…</div>
        ) : (
          <>
            <div className="tt-header">
              <h1>{topic.name}</h1>
              <span className="mono muted">
                {topic.id} · {isIntro ? "introduction" : topic.category}
                {topic.ownerGuideId && ` · owner: ${guides.find((g) => g.id === topic.ownerGuideId)?.name ?? topic.ownerGuideId}`}
              </span>
              <TopicBadges
                topic={topic}
                draft={draftVersion}
                inReview={detail.versions.find((v) => v.status === "in_review")?.version}
                approved={detail.versions.find((v) => v.status === "approved")?.version}
              />
            </div>

            <div className="toolbar">
              <select aria-label="Version" value={selected ?? ""} style={{ width: "auto", minWidth: 220 }}
                onChange={(e) => selectVersion(parseInt(e.target.value, 10))} disabled={detail.versions.length === 0}>
                {detail.versions.length === 0 && <option value="">No versions</option>}
                {detail.versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version} · {statusLabel(v.status)}
                    {v.version === topic.liveVersion ? " · live" : ""}
                    {v.version === topic.candidateVersion ? ` · candidate ${topic.rolloutPct}%` : ""}
                  </option>
                ))}
              </select>
              {editable ? (
                <>
                  <button className="btn btn-primary" onClick={save} disabled={!dirty || saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  {detail.versions.length > 1 && (
                    <button className="btn" onClick={discard} disabled={saving}>
                      Discard draft
                    </button>
                  )}
                </>
              ) : (
                <button className="btn" onClick={startDraft}
                  title={draftVersion !== null ? undefined : "Start a draft copied from the live version"}>
                  {draftVersion !== null ? `Edit draft v${draftVersion}` : "Edit"}
                </button>
              )}
              {dirty && <span className="tt-dirty">● Unsaved changes</span>}
              <div className="spacer" />
              <label className="tt-toggle">
                <input type="checkbox" checked={topic.enabled} onChange={toggleEnabled} />
                Enabled
              </label>
            </div>
            <div className={`status-line${statusError ? " error" : ""}`}>{status}</div>

            {meta && !meta.enabled && (
              <div className="callout tt-callout">
                Talk tracks are switched off on this server (TALK_TRACKS_ENABLED) — nothing is served to users.
              </div>
            )}
            {detail.versions.length === 0 && (
              <div className="callout tt-callout">This topic has no versions yet. Click Edit to start v1.</div>
            )}

            <TopicSettings key={topic.updatedAt ?? ""} topic={topic} categories={meta?.categories ?? []}
              guides={guides}
              onDelete={
                topic.liveVersion === null && topic.candidateVersion === null ? removeTopic : undefined
              }
              onSaved={(updated) => {
                setDetail((d) => (d ? { ...d, topic: updated } : d));
                setStatusMsg("Topic settings saved ✓");
              }} />

            <div className="tabs tt-tabs" role="tablist">
              {(isIntro ? INTRO_TABS : TABS).map((t) => (
                <button key={t.key} role="tab" aria-selected={tab === t.key}
                  className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
                  {t.label}{version ? tabCount(t.key) : ""}
                </button>
              ))}
            </div>
            <div className="tt-tab-body">{renderTab()}</div>
          </>
        )}
      </div>

      <div className="editor-side">
        <div className="tt-side">
          {!topic || !version ? (
            <div className="state-box">{loadError || versionError ? "—" : "Loading…"}</div>
          ) : (
            <>
              <ReviewPanel
                key={`${version.version}-${version.status}`}
                meta={meta}
                metaFailed={metaFailed}
                topic={topic}
                version={version}
                checks={checks}
                comments={comments}
                dirty={dirty}
                hasPassingSimulation={hasPassingSim}
                onFocusUnit={focusUnit}
                onComment={onComment}
                onChecks={onChecks}
                onDone={afterChange}
                onError={onError}
              />
              <RolloutPanel
                key={`${topic.candidateVersion}-${topic.rolloutPct}`}
                topic={topic}
                stats={stats}
                filtered={filtersActive(filters)}
                minUsersToPromote={meta?.minCallsToPromote ?? 0}
                onDone={afterChange}
              />
              <SimulationPanel
                topicId={topicId}
                version={version.version}
                guides={guides}
                requireSimulation={meta?.requireSimulation ?? false}
                dirty={dirty}
                onPassingChange={setHasPassingSim}
                contentHash={version.contentHash ?? null}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
